import { datadogRum } from '@datadog/browser-rum'
import Base, { ShippingState } from 'core/pages/Shipping/ShippingReading'
import { cloneDeep as _cloneDeep, get as _get, set as _set } from 'lodash'
import {
  Asn,
  ConfigurationDetail,
  DecodedItem,
  EncodingCounters,
  EncodingOperationToPerform,
  Encodings,
  EncodingValidationRequest,
  EncodingValidationResponse,
  IdentifierType,
  OperationConfigurations,
  ProductInfoField,
  ProductionOrder,
  Products,
  ShippingParcel,
  Shippings,
  TmrProduct,
  TmrTag
} from 'stylewhere/api'
import {
  AntennaButton,
  Box,
  Button,
  EncodingForm,
  EncodingIdentifers,
  EncodingProductionOrderDetail,
  FullLoadingLayer,
  OperationReadingCounters,
  OperationReadingList,
  Page,
  RemoveMode,
  Spacer,
  Stoplight,
  TagCounter,
  Text
} from 'stylewhere/components'
import { EncodingExtensions, ShippingExtensions } from 'stylewhere/extensions'
import { __, T } from 'stylewhere/i18n'
import {
  AppStore,
  FormSchema,
  FormSchemaData,
  getDataFromSchema,
  mapOperation,
  OperationReadingProps,
  OperationReadingProvider,
  RfidReader,
  Router,
  Sounds
} from 'stylewhere/shared'
import { CustomError } from 'stylewhere/shared/errors'
import { RawOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  askUserConfirmation,
  BLOCKED_ERRORS,
  checkItemsCounterError,
  filterIgnoredAndErrorItems,
  getAskUserInputEveryTime,
  getAutomaticRestartAntenna,
  getCanForceAssocation,
  getChecklistItemsCount,
  getEnableUhfWrite,
  getEncodingAssociationStatus,
  getInitialType,
  getTimeoutValidateTags,
  getWithUserConfirmationMessage,
  hasAsyncConfirm,
  isExpectOnlyMandatoryIdentifiers,
  isModalError,
  isSkipValidation,
  isSuccessEncodingCreate,
  showToast,
  showToastError,
  sleep,
  isOrderCompleted,
  isCheckEncodedQuantity,
  isCanEncodeWithOverQuantity
} from 'stylewhere/shared/utils'
import {
  getNfcReadConfiguration,
  getNfcTagReadErrors,
  isNfcAuthEnabled,
  NfcReadTracker
} from 'stylewhere/shared/nfcAuthentication'
import { v4 as generateUid } from 'uuid'

export interface WithEncodingReadingProps extends OperationReadingProps<WithEncodingReadingState> {
  parcel?: ShippingParcel
  asn?: Asn
}

export interface WithEncodingReadingState extends ShippingState {
  encodingValidation?: EncodingValidationRequest
  encodingValidationResponse?: EncodingValidationResponse
  encodingCreateResponse?: EncodingValidationResponse
  counters?: EncodingCounters
  renderIteration: number
  product?: TmrProduct
  tagsRead: TmrTag[]
  processing: boolean | 'nfcDecryption'
  associationStatus: 'TO_BE_READ' | 'CONFIRMED' | 'ERROR' | 'PROCESSING' | 'IGNORED' | 'SKIPPED' | 'WRITING' | 'READY'
  antennaOn: boolean
  forceAssociation: boolean
  pin: string
  starting: boolean
  configuration: any[]
  encodingCreateCounter: number
  resetReadingsAfterOperation: number
  formSchema: FormSchema
  encodingSchema: FormSchema
  resetProduct: boolean
  showProductionOrder: boolean
}

export default class WithEncodingReading extends Base<WithEncodingReadingProps, WithEncodingReadingState> {
  get locationState() {
    return Router.getLocationState<WithEncodingReadingState>(this.props)
  }

  embeddedEncodingOperation
  initialType
  isEncoding = false
  isDecodeNfc = false
  nfcReads = new NfcReadTracker()
  encodingStart
  timer: NodeJS.Timeout | null = null
  timerReader?: any
  timerNoRead: NodeJS.Timeout | null = null
  lastEncodedItemTime: number

  state: WithEncodingReadingState = {
    ...this.state,
    renderIteration: 0,
    formData: this.locationState.formData ?? undefined,
    parcel: this.locationState.parcel ?? undefined,
    asn: this.locationState.asn ?? undefined,
    product: undefined,
    tagsRead: [],
    processing: false,
    associationStatus: 'TO_BE_READ',
    antennaOn: false,
    forceAssociation: false,
    pin: '',
    starting: false,
    configuration: [],
    encodingCreateCounter: 0,
    formSchema: this.formSchema,
    encodingSchema: [],
    resetProduct: false,
    showProductionOrder: false
  }

  componentDidMount() {
    this._init()
  }

  _init = async () => {
    const { formData, parcel, resetProduct } = this.state
    this.isModal = isModalError(this.operation)

    const userSetAutostartAntenna: boolean =
      (await AppStore.getUserSetting('autostartAntenna', this.operation.id))?.value || this.operation.autostartAntenna
    const userSetAutoConfirmParcel: boolean =
      (await AppStore.getUserSetting('autoConfirmParcel', this.operation.id))?.value || false

    if (!this.operation.validateEncodingOperationCode) {
      showToastError(new Error('Missing embedded encoding operation code'), __(T.error.error), this.isModal)
      setTimeout(() => this.goBack(), 500)
      return
    }
    // get related encoding operation
    const encodingOperationConfig = await OperationConfigurations.search<RawOperationConfig>({
      size: 1,
      equalCodes: this.operation.validateEncodingOperationCode
    })
    if (!encodingOperationConfig?.content?.length) {
      showToastError(new Error('Missing embedded encoding operation'), __(T.error.error), this.isModal)
      setTimeout(() => this.goBack(), 500)
      return
    }
    this.embeddedEncodingOperation = mapOperation(encodingOperationConfig.content[0])

    const encodingSchema = EncodingExtensions.formSchema(this.embeddedEncodingOperation, formData)
    if (!formData) {
      if (!encodingSchema.some((e) => e.name === 'parcelCode')) {
        setTimeout(() => this.goBack(), 500)
        return
      }
    }

    const parcelEntries = this.getParcelEntries(parcel, resetProduct)
    RfidReader.initUhfTags(parcelEntries.tags)
    this.setState(
      {
        items: parcelEntries.entries,
        initReaderTags: parcelEntries.tags,
        encodingSchema: encodingSchema,
        sseStatus: !hasAsyncConfirm(this.operation),
        autostartAntenna: userSetAutostartAntenna,
        autoConfirmParcel: userSetAutoConfirmParcel,
        resetReadingsAfterOperation:
          this.state.resetReadingsAfterOperation || // leave it there if already set by Page component (handleOnUserSettingChanged)
          this.embeddedEncodingOperation.options.resetReadingsAfterOperation ||
          300
      },
      this.attachSse
    )
    this.initializeReader()
    this.initialType = getInitialType(this.embeddedEncodingOperation)
  }

  componentWillUnmount = () => {
    this.setState({ processing: false })
    this.clearTimerNoReads()
    this.clearTimerReader()
    this.clearTimer()
    RfidReader.onStartCallback = undefined
    RfidReader.onStopCallback = undefined
    void this.stopAntenna()
  }

  getAntennaCustomCommands = () => {
    return EncodingExtensions.getAntennaCustomCommandsOperationType(this.embeddedEncodingOperation)
  }

  startAntenna = async () => {
    if (!RfidReader.isReading()) {
      this.setState({ starting: true })
      RfidReader.setNfcReadConfiguration(getNfcReadConfiguration(this.embeddedEncodingOperation))
      await RfidReader.start({
        restart: getAutomaticRestartAntenna(this.embeddedEncodingOperation),
        customCommandsOperationType: this.getAntennaCustomCommands()
      })
    }
  }

  stopAntenna = async () => {
    if (RfidReader.isReading()) {
      this.setState({ starting: true })
      await RfidReader.stop({ customCommandsOperationType: this.getAntennaCustomCommands() })
    }
  }

  onBeforeInitializeReader() {
    // extensible
  }

  initializeReader = async () => {
    try {
      this.onBeforeInitializeReader()
      RfidReader.setAutomaticStop(this.embeddedEncodingOperation.autostopAntennaTimeout > 0)
      RfidReader.setAutomaticStopTime(this.embeddedEncodingOperation.autostopAntennaTimeout)
      // prefissi dell'operazione di encoding incorporata e dell'Inbound/Outbound
      RfidReader.setIgnoreReadingTagsWithPrefixes(
        [
          this.embeddedEncodingOperation.options?.ignoreReadingTagsWithPrefixes,
          this.operation.options?.ignoreReadingTagsWithPrefixes
        ]
          .filter(Boolean)
          .join(',')
      )
      RfidReader.setOnTagReadCallback(this.onTagRead)
      RfidReader.onStartCallback = this.onStartCallback
      RfidReader.onStopCallback = this.onStopCallback
    } catch (error) {
      if (!AppStore.getEmulation()) {
        showToastError((error as any).message ?? __(T.error.rfid_reader_initialization), __(T.error.error), false)
      }
    }
  }

  onStartCallback = () => {
    this.setState({ antennaOn: true, starting: false })
  }

  onStopCallbackIdentifierCheck(tmpEncoding: EncodingValidationRequest) {
    return tmpEncoding?.identifiers?.length && !tmpEncoding.identifiers.every((idt) => idt._status === 'CONFIRMED')
  }

  onStopCallback = () => {
    if (AppStore.getEmulation()) {
      this.setState({ antennaOn: false, starting: false })
      return
    }
    const tmpEncoding = this.state.encodingValidation
    RfidReader.setAutomaticStop(false)
    RfidReader.stopTimer()
    if (tmpEncoding && this.onStopCallbackIdentifierCheck(tmpEncoding)) {
      tmpEncoding.identifiers
        .filter((id) => id.identifierType !== 'SIMPLE_ITEM_IDENTIFIER')
        .filter((id) => !id._read)
        .filter((id) => id._status !== 'ERROR' && id._status !== 'NFC_COLLISION' && id._status !== 'SKIPPED')
        .forEach((idf) => {
          idf._status = 'ERROR'
          idf._error = __(T.imbustatrice.dynamic_tag_missing, { role: idf.role })
        })
      this.setState({
        antennaOn: false,
        starting: false,
        associationStatus: 'ERROR',
        encodingValidation: tmpEncoding
      })
    } else {
      this.setState({ antennaOn: false, starting: false })
    }
  }

  blankFormSchemaInput = () => {
    this.setState({ renderIteration: this.state.renderIteration + 1 })
  }

  initializeDataDogSession = (formSchemaData?: FormSchemaData) => {
    const isActive = AppStore.isDatadogActive()
    const safeFormData = formSchemaData ?? this.state.formData ?? {}
    if (isActive) {
      this.datadogSession = {
        runId: generateUid().toString(),
        operationCode: this.operation.code,
        startTime: new Date(),
        formData: safeFormData,
        initialTypeRequested: {
          label: EncodingExtensions.getDataDogFormSchemaLabel(
            this.embeddedEncodingOperation,
            safeFormData,
            this.initialType
          ),
          type: this.initialType,
          offsetMs: 0
        },
        errors: []
      }
      datadogRum.addAction('startShippingWithEncoding', this.datadogSession)
    }
  }

  getParcelEntries = (parcel: ShippingParcel, resetProduct: boolean) => {
    const entriesItem: DecodedItem[] = []
    let tagsToAdd: string[] = []
    if (!resetProduct && parcel?.parcelEntries?.length) {
      parcel.parcelEntries.forEach((entry) => {
        if (entry.item && entry.item !== null) {
          const uhfTag = entry.item.identifiers.filter((e) => e.type === 'UHF_TAG').flatMap(({ code }) => code)
          tagsToAdd = tagsToAdd.concat(uhfTag)
          entriesItem.push({
            item: entry.item,
            statuses: [],
            identifierCode: uhfTag.length > 0 ? uhfTag[0] : ''
          })
        }
      })
    }
    return {
      entries: entriesItem,
      tags: tagsToAdd
    }
  }

  onSubmitForm = async (formData: FormSchemaData) => {
    const { parcel, resetProduct } = this.state
    let newParcel = parcel
    const productionOrderRow = formData.productionOrderRow
    let tmp = this.state.formData ?? {}
    if (this.initialType === 'formSchema') {
      tmp = { ...tmp, ...formData }
    }
    if (productionOrderRow) {
      tmp.productionOrderRow = productionOrderRow.code
      if (this.initialType === 'orderRow' && productionOrderRow.productionOrder?.code)
        tmp.productionOrder = productionOrderRow.productionOrder.code
    }
    const payloadStartParcel: any = getDataFromSchema(tmp, this.getSchema())
    if (productionOrderRow?.product) {
      payloadStartParcel.productCode = productionOrderRow.product.code
    }
    if (payloadStartParcel.productionOrder) delete payloadStartParcel.productionOrder
    if (payloadStartParcel.productionOrderRow) delete payloadStartParcel.productionOrderRow

    try {
      const productCode = payloadStartParcel.product?.code ?? payloadStartParcel.productCode
      if (productCode) {
        // productCode in schema
        const encodingProductsResult = await Encodings.findProduct({
          code: productCode,
          operationId: this.embeddedEncodingOperation.id
        })
        if (!encodingProductsResult.ok) {
          const _product = await Products.searchCodes([productCode])
          if (!_product?.content?.length) {
            showToastError('Product not found', 'Product Error', this.isModal, this.blankFormSchemaInput)
            return
          }
          if (_product.content.length > 1) {
            showToastError('Multiple products found', 'Product Error', this.isModal, this.blankFormSchemaInput)
            return
          }
        }
        if (!newParcel)
          newParcel = await Shippings.startParcel(this.operation, payloadStartParcel, this.operation.startParcelWithAsn)
      } else {
        // parcelCode in schema
        if (!payloadStartParcel.parcelCode) {
          showToastError('Parcel code is required', 'Start Parcel Error', this.isModal, this.blankFormSchemaInput)
          return
        }
        if (!newParcel) {
          newParcel = await Shippings.startParcel(this.operation, payloadStartParcel, this.operation.startParcelWithAsn)
          if (!newParcel) {
            showToastError('Parcel not found', 'Start Parcel Error', this.isModal, this.blankFormSchemaInput)
            return
          }
        }
      }

      const res = await EncodingExtensions.getItemConfiguration(this.embeddedEncodingOperation, formData)
      this.initializeDataDogSession(formData)
      if (parcel) {
        res.encodingValidation.identifiers.forEach((id) => {
          id._status = 'READY'
        })
        this.setState(
          {
            associationStatus: 'READY',
            formData: tmp,
            encodingValidation: res.encodingValidation,
            encodingValidationResponse: res.encodingValidationResponse,
            counters: res.counters
          },
          () => {
            if (this.state.autostartAntenna) {
              this.startReader()
            }
          }
        )
      } else {
        const parcelEntries = this.getParcelEntries(newParcel, resetProduct)
        const startAntennaNow = this.state.autostartAntenna && parcelEntries.entries.length === 0
        if (startAntennaNow) {
          this.setState(
            {
              items: parcelEntries.entries,
              initReaderTags: parcelEntries.tags,
              associationStatus: 'TO_BE_READ',
              encodingValidation: res.encodingValidation,
              encodingValidationResponse: res.encodingValidationResponse,
              counters: res.counters,
              formData: tmp,
              parcel: newParcel
            },
            this.startReader
          )
        } else {
          res.encodingValidation.identifiers.forEach((id) => {
            id._status = 'READY'
          })
          this.setState({
            items: parcelEntries.entries,
            initReaderTags: parcelEntries.tags,
            associationStatus: 'READY',
            encodingValidation: res.encodingValidation,
            encodingValidationResponse: res.encodingValidationResponse,
            counters: res.counters,
            formData: tmp,
            parcel: newParcel
          })
        }
      }
    } catch (err) {
      if (
        await ShippingExtensions.checkPackingUnpack(
          err as any,
          payloadStartParcel.parcelCode,
          this.operation,
          () => this.onSubmitForm(formData),
          () => this.setState({ renderIteration: this.state.renderIteration + 1 })
        )
      )
        return
      if (err instanceof CustomError) {
        showToastError(
          `${CustomError.interpolateMessage(err.message, payloadStartParcel)}. [${err.errorCode}]`,
          __(T.error.error),
          this.isModal,
          this.blankFormSchemaInput
        )
        return
      }
      showToastError(err, 'Start Parcel Error', this.isModal, this.blankFormSchemaInput)
    }
  }

  startReader = async () => {
    try {
      RfidReader.setAutomaticStop(this.embeddedEncodingOperation.autostopAntennaTimeout > 0)
      RfidReader.setAutomaticStopTime(this.embeddedEncodingOperation.autostopAntennaTimeout)
      RfidReader.setNfcReadConfiguration(getNfcReadConfiguration(this.embeddedEncodingOperation))
      await RfidReader.start({
        restart: getAutomaticRestartAntenna(this.embeddedEncodingOperation),
        customCommandsOperationType: this.getAntennaCustomCommands()
      })
    } catch (error) {
      if (!AppStore.getEmulation()) {
        showToastError((error as any).message ?? __(T.error.rfid_reader_initialization), __(T.error.error), false)
      }
    }
  }

  removeTagReadFromReader = (tags: TmrTag[]) => {
    const toRemove: string[] = []
    tags.forEach((tag) => {
      toRemove.push(tag.uid ?? tag.epc)
    })
    RfidReader.removeTags(toRemove)
  }

  matchConfigurationTag = (identifiers) => {
    return false
  }

  getFullWidthAssociationBox() {
    return true
  }

  getDisableRetry() {
    return false
  }

  getUpdatedCounters = (success) => {
    const tmp = this.state.counters
    if (tmp && success) {
      if (tmp.parent) {
        tmp.parent.encoded = (tmp.parent.encoded ?? 0) + 1
      }
      if (tmp.order) {
        tmp.order.encoded = (tmp.order.encoded ?? 0) + 1
      }
      if (tmp.row) {
        tmp.row.encoded = (tmp.row.encoded ?? 0) + 1
      }
    }
    return tmp
  }

  checkAttachAutomaticReaderStop = async (encodingValidationResponse) => {
    this.isEncoding = false
    if (this.embeddedEncodingOperation.autostopAntennaTimeout > 0) {
      const blockedErrors = encodingValidationResponse
        ? encodingValidationResponse.errors?.filter((e) => BLOCKED_ERRORS.includes(e.errorCode))
        : []
      if (blockedErrors.length === 0) {
        this.attachAutomaticReaderStop()
      } else {
        this.feedbackSoundKo()
        this.setState({ associationStatus: 'ERROR' })
      }
    }
  }

  attachAutomaticReaderStop = () => {
    if (this.embeddedEncodingOperation.autostopAntennaTimeout > 0) {
      this.clearTimerReader()
      this.timerReader = setTimeout(async () => {
        if (!this.isEncoding) {
          RfidReader.isManualStop = false
          this.setState({ associationStatus: 'ERROR' })
          await this.stopAntenna()
          if (this.checkIfForceCreate('yes')) {
            this.call_force_encoding()
          } else if (this.checkIfForceCreate('withUserConfirmation')) {
            this.call_encoding_with_confirmation(false)
          } else {
            this.setState(
              (prevState) => ({
                encodingCreateCounter: prevState.encodingCreateCounter + 1
              }),
              this.create
            )
          }
        }
      }, this.embeddedEncodingOperation.autostopAntennaTimeout)
    }
  }

  getInitFormSchemaData = () => {
    const { formData, parcel } = this.state
    const data: FormSchemaData = {}
    const schema = ShippingExtensions.formSchema(this.operation, {})
    if (schema.length > 0) {
      schema.forEach((s) => {
        const value = _get(formData, s.name)
        if (value) {
          _set(data, s.name, value)
        }
      })
      // autocompletamento dei dati dalla risposta
      schema
        .filter((s) => s.copyFromResponseField)
        .forEach((s) => {
          const value = _get(parcel, s.copyFromResponseField)
          if (value) {
            _set(data, s.name, value)
          }
        })
      return data
    }

    return this.initialType === 'formSchema' &&
      this.operation.formSchema &&
      Object.keys(this.operation.formSchema).length > 0
      ? {}
      : { product: { code: '' }, wam: '' }
  }

  clearTimerNoReads = () => {
    if (this.timerNoRead) {
      clearTimeout(this.timerNoRead)
      this.timerNoRead = null
    }
  }

  feedbackSoundOk = () => {
    Sounds.success()
  }

  feedbackSoundKo = () => {
    Sounds.error()
  }

  feedbackSoundConfirmation = () => {
    Sounds.confirmation()
  }

  encodingError = (error?: any, text?: string) => {
    this.feedbackSoundKo()
    showToastError(
      error?.message ?? text ?? 'Unknown error during encoding validation',
      __(T.error.error),
      this.isModal
    )
  }

  additionalOnTagReadCheck = (tag: TmrTag) => {
    return true
  }

  getIgnoreUnknownTags = () => {
    const { options } = this.embeddedEncodingOperation
    return options?.ignoreUnknownTags === 'yes'
  }

  clearTimerReader = () => {
    if (this.timerReader) {
      clearTimeout(this.timerReader)
      this.timerReader = null
    }
  }

  clearTimer = () => {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  resetValidateResponse = () => {
    this.setState({
      encodingValidation: undefined,
      encodingValidationResponse: undefined,
      counters: undefined,
      encodingCreateResponse: undefined
    })
  }

  clear = () => {
    this.onClear()
  }

  onClearProduct = async () => {
    const { renderIteration } = this.state
    const tmp = this.state.encodingSchema
    tmp.forEach((s) => {
      if (!s.isProductCode && s.name !== 'product.code') {
        s.disabled = true
      }
    })

    const data = this.state.formData ?? {}
    if (data.product) data.product.code = ''
    data.productCode = ''
    if (data.data) delete data.data

    this.setState(
      {
        formData: data,
        renderIteration: renderIteration + 1,
        associationStatus: 'TO_BE_READ',
        pin: '',
        forceAssociation: false,
        encodingCreateCounter: 0,
        processing: false,
        encodingSchema: tmp,
        resetProduct: true
      },
      this.resetValidateResponse
    )

    this.isEncoding = false
    this.clearTimer()
    await this.stopAntenna()
  }

  onClear = async (addtags = false) => {
    const { parcel, encodingValidation, renderIteration, formData } = this.state

    const data: FormSchemaData = {}
    const schema = ShippingExtensions.formSchema(this.operation, {})
    let isParcelCode = false
    schema.forEach((s) => {
      const value = _get(formData, s.name)
      if (value) {
        if (s.name === 'parcelCode') isParcelCode = true
        _set(data, s.name, value)
      }
    })
    // autocompletamento dei dati dalla risposta
    schema
      .filter((s) => s.copyFromResponseField)
      .forEach((s) => {
        const value = _get(parcel, s.copyFromResponseField)
        if (value) {
          _set(data, s.name, value)
        }
      })

    const tmp = this.state.encodingSchema
    tmp.forEach((s) => {
      if (!s.isProductCode && s.name !== 'product.code') {
        s.disabled = false
      }
    })
    // shipping related
    if (this.isBatchReadingMode && parcel) {
      await this.updateParcel('CLEAR', [], parcel.code)
    }
    this.setState({
      items: [],
      initReaderTags: [],
      parcel: isParcelCode ? parcel : undefined,
      formData: Object.keys(data).length > 0 ? data : undefined,
      renderIteration: renderIteration + 1,
      encodingSchema: tmp,
      resetProduct: false
    })

    this.clearTimer()
    await this.stopAntenna()
    this.datadogSession = undefined
    if (!this.isBatchReadingMode) {
      RfidReader.clear()
    }

    if (addtags && encodingValidation?.identifiers) {
      RfidReader.tags = encodingValidation.identifiers
        .filter((idf) => !!idf.code)
        .map(
          (idf) =>
            ({
              uid: idf.identifierType === 'NFC_TAG' && idf.code,
              epc: idf.identifierType === 'UHF_TAG' && idf.code,
              tid: idf.tid || ''
            }) as any
        )
    }
    this.setState(
      {
        tagsRead: [],
        associationStatus: 'TO_BE_READ',
        pin: '',
        forceAssociation: false,
        encodingCreateCounter: 0,
        processing: false
      },
      this.resetValidateResponse
    )
    this.isEncoding = false
  }

  afterClearReads() {
    //extensible
  }

  clearReads = async (addtags = false, noask = false) => {
    const { encodingValidation } = this.state

    // stop reading before clearing tags to avoid double readings
    getAskUserInputEveryTime(this.embeddedEncodingOperation) && !noask && (await this.stopAntenna())

    this.clearTimer()
    this.isDecodeNfc = false
    RfidReader.clear()
    // this.datadogSession = undefined
    if (addtags && encodingValidation?.identifiers) {
      RfidReader.tags = encodingValidation.identifiers
        .filter((idf) => !!idf.code)
        .map(
          (idf) =>
            ({
              uid: idf.identifierType === 'NFC_TAG' && idf.code,
              epc: idf.identifierType === 'UHF_TAG' && idf.code,
              tid: idf.tid || ''
            }) as any
        )
    }

    if (getAskUserInputEveryTime(this.embeddedEncodingOperation) && !noask) {
      RfidReader.setAutomaticStop(false)
      this.setState(
        {
          formData: this.getInitFormSchemaData(),
          associationStatus: 'TO_BE_READ',
          tagsRead: [],
          pin: '',
          forceAssociation: false,
          encodingCreateCounter: 0,
          processing: false
        },
        this.resetValidateResponse
      )
    } else {
      //reset automatic stop antenna
      RfidReader.setAutomaticStop(this.embeddedEncodingOperation.autostopAntennaTimeout > 0)
      RfidReader.setAutomaticStopTime(this.embeddedEncodingOperation.autostopAntennaTimeout)

      //ricalcolare in base alla configurazione il reset
      const tmpEncoding = this.state.encodingValidation
      const identifiers = EncodingExtensions.resetIdentifiersEncodingValition(
        this.embeddedEncodingOperation,
        this.state.encodingValidationResponse,
        this.state.encodingValidation
      )
      if (tmpEncoding) tmpEncoding.identifiers = identifiers

      this.setState(
        {
          encodingValidation: tmpEncoding,
          encodingCreateResponse: undefined,
          associationStatus: 'TO_BE_READ',
          tagsRead: [],
          pin: '',
          forceAssociation: false,
          encodingCreateCounter: 0,
          processing: false
        },
        this.restartAntennaAfterClear
      )
    }
    this.isEncoding = false
    this.afterClearReads()
  }

  restartAntennaAfterClear = async () => {
    const { items, parcel } = this.state
    const itemsCount = items.filter(filterIgnoredAndErrorItems).length
    const { expecteds, overQty, unexpecteds } = getChecklistItemsCount(
      items,
      this.operation,
      ShippingExtensions,
      parcel?.checklist,
      parcel?.products
    )
    if (
      this.state.autostartAntenna &&
      ((expecteds > 0 && itemsCount < expecteds && !overQty && !unexpecteds) || expecteds === 0)
    ) {
      await this.startAntenna()
    }
  }

  resetAfterCreate = async () => {
    this.clearReads()
  }

  checkValidateAfterRemoveUnknownTags = () => {
    const { encodingValidation } = this.state
    if (encodingValidation) {
      const findCode = encodingValidation.identifiers.some((idt) => idt.code && idt.code !== '')
      if (findCode) {
        this.onTagReadValidate()
      } else {
        this.isEncoding = false
      }
    } else {
      this.isEncoding = false
    }
  }

  // extensible: return true (and handle stopping/state) when the read should be aborted due to too many UHF tags
  checkTooManyUhfTagsError = async (_encodingValidation: EncodingValidationRequest): Promise<boolean> => {
    return false
  }

  isNfcAuthNeededForTag = (tag: TmrTag, encodingValidationResponse?: EncodingValidationResponse) => {
    return (
      !!tag.uid &&
      isNfcAuthEnabled(this.embeddedEncodingOperation) &&
      !!encodingValidationResponse?.configuration?.details.some((conf) => conf.identifierType === 'NFC_TAG')
    )
  }

  /** Returns true if the read should be aborted (NFC auth error or collision). */
  handleNfcAuthFailure = (encodingValidation: EncodingValidationRequest): boolean => {
    const { nfcError, nfcCollision } = getNfcTagReadErrors(encodingValidation)
    if (!nfcError && !nfcCollision) {
      this.setState({ processing: false })
      return false
    }
    this.setState({ processing: false, associationStatus: 'ERROR' }, async () => {
      this.isEncoding = false
      await this.stopAntenna()
      this.clearTimer()
      this.clearTimerReader()
    })
    if (nfcCollision && this.datadogSession) {
      this.datadogSession.errors?.push({ error: 'nfcCollision' })
      datadogRum.addAction('err_nfcCollision', this.datadogSession)
    }
    return true
  }

  trackTagReadEvent = (tag: TmrTag, success: boolean) => {
    if (!this.datadogSession || success) return
    if (tag.uid) {
      if (!this.datadogSession.nfcRead) this.datadogSession.nfcRead = []
      this.datadogSession.nfcRead.push({
        offsetMs: Date.now() - this.datadogSession.startTime.getTime(),
        nfcRead: tag.uid
      })
    } else if (tag.epc) {
      if (!this.datadogSession.uhfRead) this.datadogSession.uhfRead = []
      this.datadogSession.uhfRead.push({
        offsetMs: Date.now() - this.datadogSession.startTime.getTime(),
        uhfRead: tag.epc
      })
    }
  }

  onTagRead = async (tag: TmrTag, antennaId?: string) => {
    //added a sleep to destroy correctly the timers or wait isEncoding flag
    await sleep(Math.random() * 500)

    const { encodingValidation, encodingValidationResponse, encodingCreateResponse } = this.state
    if (!encodingValidation?.identifiers) return

    if (this.isEncoding) {
      this.removeTagReadFromReader([tag])
      return
    }
    if (this.isDecodeNfc) {
      this.removeTagReadFromReader([tag])
      return
    }
    if (!this.additionalOnTagReadCheck(tag)) return
    // if time to validate is 0 then set isEncoding to true immediately for not have validated in parallel
    const timeoutValidateTags = getTimeoutValidateTags(this.embeddedEncodingOperation, this.getIgnoreUnknownTags())
    const encodingLocked = timeoutValidateTags !== undefined && timeoutValidateTags === 0
    if (encodingLocked) {
      this.isEncoding = true
    }
    const nfcRead = this.nfcReads.begin()
    try {
      RfidReader.setAutomaticStop(false)
      RfidReader.stopTimer()
      this.clearTimerReader()
      this.clearTimer()

      const nfcNeedsAuthentication = this.isNfcAuthNeededForTag(tag, encodingValidationResponse)
      if (nfcNeedsAuthentication) {
        this.isDecodeNfc = true
        nfcRead.authenticate()
        this.setState({ processing: 'nfcDecryption' })
      }

      await EncodingExtensions.onTagRead(
        encodingValidation,
        tag,
        this.embeddedEncodingOperation,
        antennaId,
        this.datadogSession
      )

      if (nfcNeedsAuthentication && this.handleNfcAuthFailure(encodingValidation)) {
        return
      }

      if (await this.checkTooManyUhfTagsError(encodingValidation)) {
        return
      }

      if (!this.lastEncodedItemTime) {
        this.lastEncodedItemTime = Date.now()
      }
      this.setState({ encodingValidation }, () => {
        this.trackTagReadEvent(tag, isSuccessEncodingCreate(encodingCreateResponse))
        this.onTagReadTimer()
      })
    } catch (error) {
      // superseded by a newer NFC authentication: the read is stale, drop it without advancing the flow
      nfcRead.isSuperseded(error)
    } finally {
      nfcRead.release((superseded) => {
        this.isDecodeNfc = false
        if (superseded) {
          this.setState({ processing: false })
          if (encodingLocked) this.isEncoding = false
        }
      })
    }
  }

  onTagReadTimer = () => {
    const timeoutValidateTags = getTimeoutValidateTags(this.embeddedEncodingOperation, this.getIgnoreUnknownTags())
    if (timeoutValidateTags !== undefined && timeoutValidateTags > 0) {
      this.timer = setTimeout(() => {
        this.onTagReadValidate()
      }, timeoutValidateTags)
    } else {
      this.onTagReadValidate()
    }
  }

  onTagReadValidate = async () => {
    const { encodingValidation } = this.state
    try {
      this.isEncoding = true
      this.encodingStart = new Date()
      const shippingDataPayload: any = this.getConfirmData()
      const res = await EncodingExtensions.validate(
        {
          ...encodingValidation,
          operationAttributes: { ...encodingValidation?.operationAttributes, ...shippingDataPayload.attributes }
        },
        true,
        false,
        getEnableUhfWrite(this.embeddedEncodingOperation) !== 'no',
        isExpectOnlyMandatoryIdentifiers(this.operation)
      )
      if (
        this.state.encodingValidation?.identifiers.some(
          (id) =>
            id.identifierType === 'NFC_TAG' &&
            id._status === 'ERROR' &&
            id._error === __(T.error.authentication_timeout)
        )
      ) {
        if (this.datadogSession) {
          this.datadogSession.errors?.push({
            error: 'nfcAuthTimeout',
            details: { nfcAuthTimeoutOffsetMs: Date.now() - this.datadogSession.startTime.getTime() }
          })
          datadogRum.addAction('err_nfcAuthTimeout', this.datadogSession)
        }
      }
      this.setState(
        {
          encodingValidationResponse: res.encodingValidationResponse,
          encodingValidation: res.encodingValidation
        },
        this.onTagReadValidateResponse
      )
    } catch (error) {
      this.clearReads(false, true)
      this.encodingError(error, 'Unknown error during encoding validation')
    } finally {
      this.isEncoding = false
    }
  }

  getErrorCodesToWhichStopAntenna = () => {
    return ['EXTRA_TAG']
  }

  onTagReadValidateResponse = async () => {
    const { encodingValidationResponse, encodingValidation, encodingCreateCounter } = this.state
    if (encodingValidationResponse) {
      if (encodingValidationResponse.errors && encodingValidationResponse.errors.length > 0) {
        // Fail encoding based on error codes
        if (
          ['EXTRA_TAG', 'UNEXPECTED_TAG'].some((errorCode) =>
            encodingValidationResponse.errors.some((el) => el.errorCode === errorCode)
          )
        ) {
          this.encodingFail()
          return
        }

        if (encodingValidationResponse.errors.some((el) => el.errorCode === 'OTHER_PRODUCTION_ORDER')) {
          showToastError(__(T.error.other_production_order), __(T.error.error), this.isModal)
          this.encodingFail()
          return
        }

        // if there are MISSING_MANDATORY_TAG, MISSING_MANDATORY_TAG_DUAL errors we are waiting for the other requested tags
        const checkMissing = encodingValidationResponse.errors.some((el) =>
          ['MISSING_MANDATORY_TAG', 'MISSING_MANDATORY_TAG_DUAL'].includes(el.errorCode)
        )
        if (checkMissing) {
          this.isEncoding = false
          return
        }
      }

      // controllo se non devo fare operazioni di scrittura
      if (encodingValidationResponse.operationToPerform === 'NONE') {
        await this.onValidationOperationNone()
      } else if (
        ['UPDATE_ONLY_PRODUCT', 'UPDATE_WAM', 'UPDATE_PRODUCTION_ORDER_ROW'].includes(
          encodingValidationResponse.operationToPerform
        )
      ) {
        Sounds.confirmation()

        const readTag = encodingValidation?.identifiers
          ? encodingValidation?.identifiers.length === 1
            ? encodingValidation?.identifiers[0]
            : encodingValidation?.identifiers?.length > 1
              ? encodingValidation?.identifiers.find((id) => id._status === 'CONFIRMED')
              : undefined
          : undefined
        const responseItemIds = encodingValidationResponse?.item?.identifiers
        const responseItemId = responseItemIds
          ? responseItemIds.length === 1
            ? responseItemIds[0]
            : responseItemIds?.length > 1
              ? responseItemIds.find((id) => id.code === readTag.code)
              : undefined
          : undefined

        const modalMessage: Partial<Record<EncodingOperationToPerform, string>> = {
          UPDATE_ONLY_PRODUCT: __(T.misc.update_only_product),
          UPDATE_WAM: __(T.misc.update_only_product),
          UPDATE_PRODUCTION_ORDER_ROW: __(T.misc.update_production_order_row)
        }

        const ok = await askUserConfirmation(
          __(T.misc.attention),
          modalMessage[encodingValidationResponse.operationToPerform] ??
            __(T.messages.are_you_sure_you_want_to_confirm),
          undefined,
          undefined,
          readTag
            ? {
                identifierCode: readTag.code,
                productionOrderRowId: responseItemId?.productionOrderRowId
              }
            : undefined
        )
        if (ok) {
          this.setState({ encodingCreateCounter: encodingCreateCounter + 1 }, this.call_force_encoding)
        } else {
          this.onClearProduct()
          this.isEncoding = false
          await this.sendMixpanelEventData('Encoding: Update product not confirmed')
        }
      } else if (
        encodingValidationResponse.operationToPerform === 'FAIL' ||
        encodingValidationResponse.operationToPerform.startsWith('FAIL_')
      ) {
        await this.sendMixpanelEventData('Encoding: error')
        this.encodingFail()
      } else if (encodingValidationResponse.operationToPerform.startsWith('FORCE_')) {
        this.clearTimerReader()
        this.checkEncodingForceOption()
      } else {
        this.setState({ encodingCreateCounter: encodingCreateCounter + 1 }, this.create)
      }
    }
  }

  encodingFail = async () => {
    const { encodingValidation } = this.state
    encodingValidation.identifiers.forEach((idf) => {
      if (['PROCESSING', 'TO_BE_READ'].includes(idf._status)) {
        idf._status = 'SKIPPED'
      }
    })
    this.setState({ associationStatus: 'ERROR', processing: false })
    await this.stopAntenna()
    this.isEncoding = false
    this.isDecodeNfc = false
    this.feedbackSoundKo()
  }

  addTagToList = (encodingValidationResponse: EncodingValidationResponse) => {
    const tag: any = {}
    const epc = encodingValidationResponse.item.identifiers.find((idf) => idf.type === 'UHF_TAG')?.code
    const nfc = encodingValidationResponse.item.identifiers.find((idf) => idf.type === 'NFC_TAG')?.code
    if (epc) tag.epc = epc
    if (nfc) tag.uid = nfc

    if (this.datadogSession) {
      const tagCode =
        encodingValidationResponse.item.identifiers.find((idf) => idf.type === 'UHF_TAG')?.code ??
        encodingValidationResponse.item.identifiers.find((idf) => idf.type === 'NFC_TAG')?.code ??
        ''
      if (!this.datadogSession.additionalInfo) this.datadogSession.additionalInfo = {}
      this.datadogSession.additionalInfo.tagValidated = {
        offsetMs: Date.now() - this.datadogSession.startTime.getTime(),
        code: tagCode
      }
      if (!this.datadogSession.additionalInfo.readItems) this.datadogSession.additionalInfo.readItems = []
      this.datadogSession.additionalInfo.readItems.push({
        nfc: nfc,
        epc: epc,
        duration: Date.now() - this.lastEncodedItemTime,
        offset: Date.now() - this.datadogSession.startTime.getTime()
      })
      this.lastEncodedItemTime = undefined
      datadogRum.addAction('shippingWithEncodingTagValidated', this.datadogSession)
    }

    // Temporarily switch to decode mode to push the newly associated tag into shipping list.
    // Then restore the encoding callback.
    RfidReader.onTagReadCallback = undefined
    this.setRfidReaderDecode()
    RfidReader.removeTags([epc ?? '', nfc ?? ''])
    RfidReader.onTagRead(JSON.stringify(tag))
    RfidReader.setOnTagReadCallback(this.onTagRead)
  }

  onValidationOperationNone = async () => {
    const { encodingValidationResponse } = this.state
    await this.stopAntenna()
    RfidReader.clear()
    this.setState({
      processing: false,
      associationStatus: 'SKIPPED'
    })
    if (this.embeddedEncodingOperation.options.onItemAlreadyEncodedFeedback === 'confirmModal') {
      AppStore.showFeedbackModal(
        __(T.misc.attention),
        __(T.messages.encoding_success_none),
        () => {},
        __(T.misc.confirm)
      )
    } else {
      showToast({ status: 'success', description: __(T.messages.encoding_success_none) })
    }
    this.feedbackSoundOk()

    if (encodingValidationResponse) {
      // if item is already associated we should list it
      this.addTagToList(encodingValidationResponse)
    }

    setTimeout(async () => {
      this.resetAfterCreate()
    }, this.state.resetReadingsAfterOperation || 300)
  }

  checkIfForceCreate = (value) => {
    const { encodingValidationResponse } = this.state
    let canForce = false
    if (getCanForceAssocation(this.embeddedEncodingOperation) === value) {
      const notForcible = encodingValidationResponse?.errors?.find((e) => !e.forcible)
      //if only errors with forcible = true then force create
      canForce = !notForcible
    }
    return canForce
  }

  checkEncodingForceOption = async () => {
    const { encodingValidationResponse } = this.state
    if (encodingValidationResponse) {
      const errors = encodingValidationResponse.errors ?? []
      const errorCodesToWhichStopAntenna = this.getErrorCodesToWhichStopAntenna()
      if (errors.some((e) => errorCodesToWhichStopAntenna.includes(e.errorCode))) {
        this.encodingFail()
        return
      }
      if (this.checkIfForceCreate('yes')) {
        this.call_force_encoding()
      } else if (this.checkIfForceCreate('withUserConfirmation')) {
        this.call_encoding_with_confirmation(true)
      } else {
        this.checkAttachAutomaticReaderStop(encodingValidationResponse)
      }
    } else {
      this.feedbackSoundKo()
      this.setState({ processing: false })
      this.isEncoding = false
    }
  }

  call_encoding_with_confirmation = async (datadog: boolean) => {
    const { encodingValidationResponse } = this.state
    const errors = encodingValidationResponse?.errors ?? []
    // if only error with code MISSING_OPTIONAL_TAG and isExpectOnlyMandatoryIdentifiers do force create
    if (
      errors.length == 1 &&
      errors[0].errorCode === 'MISSING_OPTIONAL_TAG' &&
      isExpectOnlyMandatoryIdentifiers(this.embeddedEncodingOperation)
    ) {
      this.call_force_encoding()
    } else {
      const ok = await askUserConfirmation(
        __(T.misc.attention),
        getWithUserConfirmationMessage(errors, encodingValidationResponse)
      )

      if (datadog && this.datadogSession) {
        const reassociationError = errors.some(
          (e) => e.errorCode === 'TAG_ALREADY_ASSOCIATED' || e.errorCode === 'ALREADY_ASSOCIATED_TAG'
        )
        if (reassociationError) {
          if (!this.datadogSession.additionalInfo) this.datadogSession.additionalInfo = {}
          if (!this.datadogSession.additionalInfo.reassociations) this.datadogSession.additionalInfo.reassociations = []
          const alreadyAssociatedIdentifiers = encodingValidationResponse.errors.map((e) => ({
            code: e.payload?.code,
            type: e.payload?.identifierType
          }))
          this.datadogSession.additionalInfo.reassociations.push({
            userChoice: ok ? 'ok' : 'cancel',
            alreadyAssociatedIdentifiers,
            autoStartAntenna: this.state.autostartAntenna,
            offset: Date.now() - this.datadogSession.startTime.getTime()
          })
          datadogRum.addAction('tagReassociation', this.datadogSession)
        }
      }

      if (ok) {
        this.call_force_encoding()
      } else {
        this.clear()
        this.isEncoding = false
      }
    }
  }

  call_force_encoding = () => {
    this.isEncoding = true
    this.encodingStart = new Date()
    this.setState(
      (prevState) => ({
        forceAssociation: true,
        pin: '',
        encodingCreateCounter: prevState.encodingCreateCounter + 1
      }),
      this.create
    )
  }

  sendMixpanelEventData = async (event) => {
    const { encodingValidation, encodingValidationResponse, forceAssociation } = this.state
    const endDate = new Date()
    const seconds = this.encodingStart ? (endDate.getTime() - this.encodingStart.getTime()) / 1000 : 0
    const mixpanelData = EncodingExtensions.getMixPanelData(
      this.embeddedEncodingOperation,
      encodingValidation,
      encodingValidationResponse,
      seconds,
      forceAssociation ? 'yes' : 'no'
    )
    AppStore.sendMixPanelEvent(event, mixpanelData)
  }

  parcelIsSuitableForConfirmation = () => {
    const { parcel, items } = this.state
    if (!parcel) return false
    const itemsCount = items.filter(filterIgnoredAndErrorItems).length
    const { expecteds, overQty, unexpecteds } = getChecklistItemsCount(
      items,
      this.operation,
      ShippingExtensions,
      parcel.checklist,
      parcel.products
    )
    return itemsCount === expecteds && !overQty && !unexpecteds
  }

  onParcelConfirmIsSuitable = async () => {
    if (this.state.autoConfirmParcel) {
      await this.antennaRef.current.stopReader()
      RfidReader.setAutomaticStop(false)
      RfidReader.stopTimer()
      setTimeout(
        () => {
          this.setState({ confirming: true }, () => {
            this.onConfirm()
          })
        },
        Math.max(this.state.resetReadingsAfterOperation, 1500) - 1500
      )
    }
  }

  create = async () => {
    const { forceAssociation, pin, encodingValidation, encodingValidationResponse } = this.state
    if (encodingValidation) {
      await this.stopAntenna()
      const skipValidation = isSkipValidation(this.embeddedEncodingOperation)
      try {
        let res
        const shippingDataPayload: any = this.getConfirmData()
        if (forceAssociation) {
          res = await EncodingExtensions.force_create(
            { ...encodingValidation, itemAttributes: shippingDataPayload.attributes ?? {} },
            pin
          )
        } else
          res = await EncodingExtensions.create({
            ...encodingValidation,
            itemAttributes: shippingDataPayload.attributes ?? {}
          })
        if (!res) throw new Error(__(T.error.item_creation_error))

        const success = isSuccessEncodingCreate(res)
        const updatedCounters = this.getUpdatedCounters(success)
        if (skipValidation) {
          await EncodingExtensions.set_identifiers_status(
            encodingValidation,
            res,
            getEnableUhfWrite(this.embeddedEncodingOperation) !== 'no',
            isExpectOnlyMandatoryIdentifiers(this.operation)
          )
          this.setState({
            encodingCreateResponse: res,
            encodingValidationResponse: res,
            encodingValidation: encodingValidation,
            processing: false,
            associationStatus: getEncodingAssociationStatus(res, encodingValidationResponse),
            counters: updatedCounters
          })
        } else {
          this.setState({
            encodingCreateResponse: res,
            processing: false,
            associationStatus: getEncodingAssociationStatus(res, encodingValidationResponse),
            counters: updatedCounters
          })
        }
        if (success) {
          await this.sendMixpanelEventData('Encoding: Item Associated')
          await AppStore.increaseDailyEncodedItems(this.embeddedEncodingOperation.code)
          showToast({ status: 'success', description: __(T.messages.encoding_success) })
          this.feedbackSoundOk()

          // hook up with shipping reading method, adding tag to the list
          this.addTagToList(res)

          //if success reset operation after time in milliseconds
          setTimeout(
            async () => {
              this.resetAfterCreate()
            },
            this.state.resetReadingsAfterOperation || (skipValidation ? 4000 : 300)
          )
        } else {
          // await this.sendMixpanelEventData('Encoding: error')
          this.encodingError(undefined, __(T.error.item_creation_error))

          this.isEncoding = false
          if (!forceAssociation) {
            await this.startAntenna()
          }
          this.setState({ processing: false })
        }
      } catch (error) {
        this.setState({ processing: false })
        this.attachAutomaticReaderStop()
        if (!forceAssociation) {
          await this.startAntenna()
        }
        this.isEncoding = false
        this.encodingError(error, 'Unknown error during encoding validation')
      }
    } else {
      this.setState({ processing: false })
      this.encodingError(undefined, __(T.error.item_creation_error))
    }
  }

  _confirmSuccess = async (result?, lastParcel?: boolean) => {
    this.onConfirmSuccess(result)
    const confirmData = this.getConfirmData()
    await ShippingExtensions.afterConfirm(this.operation, confirmData, result || {})
    this.setState({ confirming: false })
    if (result?.status === 'READY_TO_RECEIVE' || result?.status === 'RECEIVED') {
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.parcel_success),
        status: 'success'
      })
    } else {
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.generic_success, { code: this.operation.description }),
        status: 'success'
      })
    }

    if (this.datadogSession) {
      this.datadogSession.endTime = new Date()
      this.datadogSession.operationSuccessOffsetMs =
        this.datadogSession.endTime.getTime() - this.datadogSession.startTime.getTime()
      if (!this.datadogSession.additionalInfo) this.datadogSession.additionalInfo = {}
      this.datadogSession.additionalInfo.autoConfirmParcel = this.state.autoConfirmParcel
      this.datadogSession.additionalInfo.readParcelItemsQty = this.state.items.filter(filterIgnoredAndErrorItems).length
      datadogRum.addAction('shippingWithEncodingSuccess', this.datadogSession)
    }

    const encodingSchema = EncodingExtensions.formSchema(this.embeddedEncodingOperation, {})
    const find = encodingSchema.some((e) => e.name === 'parcelCode')
    if (find) {
      this.onClear()
    } else {
      const { asn } = this.state
      if (
        (this.operation.shippingMode === 'shipment' || !this.operation.autoconfirmAsnOnParcelConfirmation) &&
        asn &&
        lastParcel &&
        this.operation.askToCloseShipmentOnLastParcel !== 'no'
      ) {
        if (this.operation.askToCloseShipmentOnLastParcel === 'askUserConfirmation') {
          if (
            await askUserConfirmation(
              __(T.confirm.post_confirm_action_title),
              __(T.confirm.last_parcel_confirmerd, {
                code: asn.code
              }),
              __(T.misc.no),
              __(T.misc.yes)
            )
          ) {
            this.confirmCloseAsn()
          } else {
            this.setState({ confirming: false })
            this.goBack()
          }
        } else {
          this.confirmCloseAsn()
        }
      } else {
        this.setState({ confirming: false })
        this.afterConfirmSuccess()
      }
    }
  }

  getProcessingMessage() {
    return __(this.state.processing === 'nfcDecryption' ? T.messages.nfcDecryption : T.messages.operation_in_progress)
  }

  goBack = () => {
    const filterParams = this.locationState?.filterParams ?? {}
    if (this.locationState?.backRoute) {
      Router.navigate(
        this.locationState?.backRoute,
        { ...this.locationState?.backRouteParams },
        { state: { filterParams: { ...filterParams } } }
      )
    } else {
      const schema = ShippingExtensions.formSchema(this.operation, {})
      if (schema.length > 0) {
        Router.navigate('/shipping/withEncoding/:opCode', { opCode: this.operation.code, ...filterParams })
      } else {
        this.goDashboard()
      }
    }
  }

  isProductCode = () => {
    const { encodingSchema } = this.state
    return this.initialType === 'product' || encodingSchema.find((s) => s.isProductCode)
  }

  getProductCode = () => {
    const { formData, formSchema } = this.state
    let productCode
    if (formData) {
      const payloadStartParcel: any = getDataFromSchema(formData, formSchema)
      productCode = payloadStartParcel.product?.code ?? payloadStartParcel.productCode
      if (payloadStartParcel.productionOrderRow?.product) {
        productCode = payloadStartParcel.productionOrderRow.product.code
      }
    }
    return productCode
  }

  showClearAndConfirm() {
    const { confirming, parcel, items, autoConfirmParcel, formData, initReaderTags } = this.state
    const showClearButton =
      (parcel && items.length > 0 && this.isBatchReadingMode) || (!!formData && this.state.encodingValidation)
    const showConfirmButton = this.canShowConfirmButton()
    const confirmButtonEnabled = this.confirmButtonEnabled()
    const autoConfirmPlainParcelMode = autoConfirmParcel && initReaderTags.length === 0
    return (
      <Box row mt={showClearButton || showConfirmButton ? 20 : 0}>
        {showClearButton && (
          <Button
            flex
            variant="default"
            style={{ marginRight: showClearButton && showConfirmButton ? 10 : 0 }}
            title={__(T.misc.clear)}
            onClick={this.onClear}
          />
        )}
        {showConfirmButton && (
          <Button
            flex
            style={{ marginTop: 0 }}
            variant={autoConfirmPlainParcelMode ? 'plain' : confirmButtonEnabled ? 'primary' : 'ignore'}
            loading={confirming}
            disabled={!confirmButtonEnabled}
            title={autoConfirmPlainParcelMode ? __(T.misc.confirm_auto) : __(T.misc.confirm)}
            onClick={this.onConfirm}
          />
        )}
      </Box>
    )
  }

  getFilteredIdentifiersToRender = (identifiers: ConfigurationDetail[]) => {
    return identifiers
  }

  getIdentifiersLabelsByType: () => Partial<Record<IdentifierType, string>> | undefined = () => {
    return undefined
  }

  getProductInfoFields: () => ProductInfoField[] | undefined = () => {
    return undefined // use defaults
  }

  getHideIdentifierByType = (): IdentifierType[] | undefined => {
    return undefined
  }

  getHideIdentifierCodeByType = (): IdentifierType[] | undefined => {
    return undefined
  }

  getHideIdentifierZones: () => boolean = () => {
    return false
  }

  handleOnUserSettingChanged(settingName: string, newValue: string | number | boolean) {
    super.handleOnUserSettingChanged(settingName, newValue)
    if (settingName === 'resetReadingsAfterOperation') {
      this.setState({ resetReadingsAfterOperation: Number(newValue) })
    }
  }

  getAntennaButtonCustomCommandsOperationType = () => {
    return this.getAntennaCustomCommands()
  }

  getCustomSidebarBottomComponent = () => {
    return null
  }

  enabledResetOnlyProduct = () => {
    return true
  }

  getSidebarWidth = () => {
    return 500
  }

  getSchema = () => {
    const { encodingSchema, parcel } = this.state
    const schema: FormSchema = []
    const shipping = ShippingExtensions.formSchema(this.operation, { parcel: parcel }, ['', 'parcel'])
    shipping.forEach((s) => {
      if (s.isProductCode) {
        if (!this.isProductCode()) schema.push(s)
      } else {
        schema.push(s)
      }
    })
    if (this.embeddedEncodingOperation && !getAskUserInputEveryTime(this.embeddedEncodingOperation)) {
      encodingSchema.forEach((s) => {
        if (s.name === 'product.code' || s.isProductCode) {
          s.label = __(T.fields.product_code)
        }
        schema.push(s)
      })
    }
    return schema
  }

  onProductionOrderReceived = async (productionOrder: ProductionOrder) => {
    try {
      const countersResponse = await Encodings.getProductionOrderCounter(productionOrder.id)
      this.setState({ counters: countersResponse?.data }, this.checkProductionOrder)
    } catch (error) {
      showToastError(error, __(T.error.error), this.isModal)
    }
  }

  checkProductionOrder = () => {
    const { counters } = this.state
    if (
      isOrderCompleted(
        counters,
        isCheckEncodedQuantity(this.embeddedEncodingOperation),
        isCanEncodeWithOverQuantity(this.embeddedEncodingOperation)
      )
    ) {
      showToastError(__(T.error.production_order_completed), __(T.error.error), this.isModal)
    }
  }

  showProductionOrderDetail = () => {
    if (this.hasProductionOrder()) {
      this.setState({ showProductionOrder: true })
    }
  }

  closeProductionOrderDetail = () => {
    this.setState({ showProductionOrder: false })
  }

  hasProductionOrder = () => {
    const { formData } = this.state
    return formData && (formData.productionOrder || formData.productionOrderRow?.productionOrder)
  }

  getProductionOrder = () => {
    const { formData } = this.state
    if (!formData) return {}
    if (formData.productionOrderRow) {
      return formData.productionOrderRow.productionOrder ?? {}
    }
    return formData.productionOrder ?? {}
  }

  render() {
    const {
      items,
      formData,
      loading,
      parcel,
      product,
      sseStatus,
      sseError,
      processing,
      renderIteration,
      removeModeEnabled,
      encodingValidation,
      encodingValidationResponse,
      associationStatus,
      initReaderTags,
      encodingSchema,
      counters,
      showProductionOrder
    } = this.state

    let tagCounterExpecteds = 0
    let tagCounterUnexpecteds = 0
    if (parcel?.checklist) {
      const { expecteds, unexpecteds, overQty } = getChecklistItemsCount(
        items,
        this.operation,
        ShippingExtensions,
        parcel.checklist,
        parcel.products
      )
      tagCounterExpecteds = expecteds
      tagCounterUnexpecteds = unexpecteds + overQty
    }
    const identifiers = encodingValidation?.identifiers
    const detected = items
      .filter(filterIgnoredAndErrorItems)
      .reduce((acc, item) => acc + OperationReadingProvider.getItemIntrinsicQuantity(item, ShippingExtensions), 0)
    const encodingProduct = product ?? EncodingExtensions.getEncodingProduct(encodingValidationResponse)
    return (
      <Page
        title={this.operation.description}
        onBackPress={() => this.goBack()}
        alertOnBackPress={
          this.operation.showAlertOnExit && !this.isBatchReadingMode && items.some(filterIgnoredAndErrorItems)
        }
        loading={loading}
        headerRight={
          <Box style={{ flexDirection: 'row' }}>
            {hasAsyncConfirm(this.operation) ? <Stoplight active={sseStatus} /> : undefined}
            {this.operationRemoveModeAllowed && <RemoveMode onRemoveChange={this.onRemoveChange} />}
          </Box>
        }
        header={{
          details: {
            data: _cloneDeep(formData),
            formSchema: this.getSchema(),
            asn: this.asn,
            operationId: this.operation.id
          }
        }}
        forceDetails="top"
        userSettings={{ operation: this.operation, settings: this.operation.userSettings }}
        onUserSettingChanged={(name, newValue) => this.handleOnUserSettingChanged(name, newValue)}
        enableEmulation
        activeAntennaSettings
      >
        {!sseError && (
          <>
            <Page.Sidebar width={this.getSidebarWidth()}>
              <Box flex>
                {this.embeddedEncodingOperation && (
                  <EncodingForm
                    key={renderIteration}
                    overrideFormSchema={this.initialType === 'formSchema' ? encodingSchema : undefined}
                    overrideProductInfoFields={this.getProductInfoFields()}
                    onSubmit={this.onSubmitForm}
                    product={encodingProduct}
                    operation={this.embeddedEncodingOperation}
                    data={formData}
                    counters={counters}
                    onProductionOrderReceived={this.onProductionOrderReceived}
                    updateFormSchemaData={(data) => {
                      this.setState({
                        formData: { ...this.state.formData, productionOrder: data.productionOrder.code }
                      })
                    }}
                    onProductionOrderClear={this.onClear}
                    onProductClear={
                      this.enabledResetOnlyProduct() && this.isProductCode() ? this.onClearProduct : undefined
                    }
                    disabledClear
                    disabledClearProduct={!this.enabledResetOnlyProduct() || !this.isProductCode()}
                    style={{ width: '100%' }}
                    flex={false}
                    productionOrderDetail={this.hasProductionOrder() ? this.showProductionOrderDetail : undefined}
                  />
                )}
                {formData && encodingProduct && (
                  <AntennaButton
                    ref={this.antennaRef}
                    decodeRequest={this.getDecodeRequest()}
                    onItemDecoded={this.onDecodedItemCallback}
                    onTagReadCallback={this.onTagRead}
                    onClear={this.onClear}
                    initReaderTags={initReaderTags}
                    hideClear
                    customCommandsOperationType={this.getAntennaButtonCustomCommandsOperationType()}
                    onStart={async () => {
                      RfidReader.onStopCallback = this.onStopCallback
                      const res = await EncodingExtensions.getItemConfiguration(
                        this.embeddedEncodingOperation,
                        formData
                      )
                      RfidReader.clear()
                      RfidReader.setAutomaticStop(this.embeddedEncodingOperation.autostopAntennaTimeout > 0)
                      RfidReader.setAutomaticStopTime(this.embeddedEncodingOperation.autostopAntennaTimeout)
                      if (!this.datadogSession) this.initializeDataDogSession(formData)
                      this.setState({
                        associationStatus: 'TO_BE_READ',
                        encodingValidation: res.encodingValidation,
                        encodingValidationResponse: res.encodingValidationResponse
                      })
                    }}
                    overrideStartLabel={associationStatus === 'ERROR' ? __(T.misc.retry) : __(T.misc.start)}
                  />
                )}
                <Spacer />
                <TagCounter
                  detected={detected}
                  expected={tagCounterExpecteds}
                  unexpected={tagCounterUnexpecteds}
                  layout={{ disableSpacer: true }}
                />
                {checkItemsCounterError(items) && (
                  <>
                    <Spacer />
                    <OperationReadingCounters
                      operation={this.operation}
                      items={items}
                      hideIdentifiersByType={this.getHideIdentifierByType()}
                      hideIdentifiersZones={this.getHideIdentifierZones()}
                      overrideProductInfoFields={this.getProductInfoFields()}
                      disableModalRowCounters
                    />
                  </>
                )}
              </Box>
              {this.showClearAndConfirm()}
              {this.getCustomSidebarBottomComponent()}
            </Page.Sidebar>
            <Page.Content notBoxed>
              {identifiers && encodingValidation && (
                <EncodingIdentifers
                  identifiers={this.getFilteredIdentifiersToRender(identifiers)}
                  clearReads={() => {
                    this.clearReads()
                  }}
                  onTagRead={this.onTagRead}
                  associationStatus={associationStatus}
                  disabledRetry={this.getDisableRetry()}
                  lineOfMax3Identifiers
                  layout={{ minHeight: '12vh', maxHeight: '12vh', iconSize: 60 }}
                  marginBottom={10}
                  fullWidthAssociationBox={this.getFullWidthAssociationBox()}
                  labelsByType={this.getIdentifiersLabelsByType()}
                  hideIdentifierCodeByType={this.getHideIdentifierCodeByType()}
                />
              )}
              {formData && encodingProduct && <Box style={{ height: 50 }} />}
              <OperationReadingList
                removeItemCallback={this.removeItem}
                extension={ShippingExtensions}
                items={items}
                operation={this.operation}
                checklist={parcel ? parcel.checklist : undefined}
                products={parcel ? parcel.products : undefined}
                removeModeEnabled={removeModeEnabled && this.isRemoveModeBySku}
                emptyMessage={null}
                hideIdentifiersByType={this.getHideIdentifierByType()}
                hideIdentifiersZones={this.getHideIdentifierZones()}
                overrideProductInfoFields={this.getProductInfoFields()}
              />
            </Page.Content>
          </>
        )}
        {sseError && (
          <Box flex center>
            <Text bold fontSize={20}>
              {__(T.error_messages.connection_in_progress_title)}
            </Text>
            <Text style={{ marginTop: 10 }}>{__(T.error_messages.connection_in_progress_text)}</Text>
          </Box>
        )}
        {!sseStatus && !sseError && (
          <FullLoadingLayer
            message={sseStatus ? __(T.messages.operation_in_progress) : __(T.messages.connection_in_progress)}
          />
        )}
        {processing && <FullLoadingLayer message={this.getProcessingMessage()} />}

        {showProductionOrder && (
          <EncodingProductionOrderDetail
            productionOrder={this.getProductionOrder()}
            onClose={this.closeProductionOrderDetail}
          />
        )}
      </Page>
    )
  }
}
